import { StringHelper } from "./string-helper";
import { PeriodHelper } from "./period-helper";
import { InvalidParameterError } from "../core/exception/invalid-parameter-error";
import { DateTime } from "luxon";
import { UrlHelper } from "../core/html/url-helper";

export class DateHelper {
	public static readonly dayInMs = 1000 * 60 * 60 * 24;
	public static readonly ms = new Date().getTime();

	public static addDays(date: Date | string, days: number): Date {
		if (!date) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}

		if (!(date instanceof Date)) {
			return this.addDays(new Date(date), days);
		}

		let dateInMs = date.getTime();
		dateInMs += (DateHelper.dayInMs * days);
		return new Date(dateInMs);
	}

	public static getDateWithoutTime(date: Date | string): Date {
		if (!date) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}

		if (!(date instanceof Date)) {
			return this.getDateWithoutTime(new Date(date));
		}

		return new Date(date.toDateString());
	}

	public static isWeekend(date: Date | string, weekendDays: number[] = [0, 6]): boolean {
		if (date == null) {
			return false;
		}
		if (!(date instanceof Date)) {
			return this.isWeekend(new Date(date), weekendDays);
		}

		let day = date.getDay();
		return weekendDays.indexOf(day) !== -1;
	}

	public static dateToFilename(date: Date = DateHelper.nowAsDateWithTime()): string {
		return `${date.getFullYear()}${StringHelper.pad(date.getMonth() + 1)}${StringHelper.pad(date.getDate())}`;
	}

	public static fullDateToFilename(date: Date = DateHelper.nowAsDateWithTime()): string {
		return `${DateHelper.dateToFilename(date)}_${StringHelper.pad(date.getHours())}${StringHelper.pad(date.getMinutes())}`;
	}

	public static dateToGermanZeroLeadingString(date: Date | string, withYear: boolean = true): string {
		if (!date) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}

		if (!(date instanceof Date)) {
			return this.dateToGermanZeroLeadingString(new Date(date), withYear);
		}

		return withYear ? `${StringHelper.pad(date.getDate())}.${StringHelper.pad(date.getMonth() + 1)}.${StringHelper.pad(date.getFullYear())}`
			: `${StringHelper.pad(date.getDate())}.${StringHelper.pad(date.getMonth() + 1)}.`;
	}

	public static dateToGermanZeroLeadingStringOrDefault(date: Date | string, withYear: boolean = true, defaultValue: string = null): string {
		if (!date) {
			return defaultValue;
		}

		return DateHelper.dateToGermanZeroLeadingString(date, withYear);
	}

	public static getTime(date: Date | string): number {
		if (!date) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}

		if (!(date instanceof Date)) {
			return this.getTime(new Date(date));
		}

		return date.getTime();
	}

	public static datetimeToGermanZeroLeadingString(date: Date | string): string {
		if (!date) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}

		if (!(date instanceof Date)) {
			return this.datetimeToGermanZeroLeadingString(new Date(date));
		}
		return `${StringHelper.pad(date.getDate())}.${StringHelper.pad(date.getMonth() + 1)}.${StringHelper.pad(date.getFullYear())} ${StringHelper.pad(date.getHours())}:${StringHelper.pad(date.getMinutes())}`;
	}

	public static datetimeToGermanZeroLeadingStringOrDefault(date: Date | string, defaultValue: string = "N/A"): string {
		if (!date) {
			return defaultValue;
		}
		return DateHelper.datetimeToGermanZeroLeadingString(date);
	}

	public static isDateWithoutTimeInFuture(date: Date | string): boolean {
		if (!date) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}

		return DateHelper.getDateWithoutTime(date) > DateHelper.today();
	}

	public static isDateWithoutTimeInFutureOrNull(date: Date): boolean {
		if (date == null) {
			return true;
		}
		return this.isDateWithoutTimeInFuture(date);
	}

	public static isDateWithoutTimeInFutureOrToday(date: Date | string): boolean {
		if (!date) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}

		return DateHelper.getDateWithoutTime(date) >= DateHelper.today();
	}

	public static isDateWithoutTimeInPast(date: Date | string): boolean {
		if (!date) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}
		return DateHelper.getDateWithoutTime(date) < DateHelper.today();
	}

	public static isDateWithoutTimeInPastOrToday(date: Date): boolean {
		if (!date) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}
		return DateHelper.getDateWithoutTime(date) <= DateHelper.today();
	}

	public static periodString(from: Date | string, to: Date | string = null): string {
		return PeriodHelper.toString(from, to);
	}

	public static dateWithoutTimePlusDays(date: Date, days: number): Date {
		if (!date) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}
		if (days === 0) {
			return date;
		}
		let result = DateHelper.getDateWithoutTime(date);
		result.setDate(result.getDate() + days);
		return result;
	}

	public static totalDaysOfDateWithoutTime(date: Date): number {
		if (!date) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}

		let today = DateHelper.getDateWithoutTime(date);
		return Math.round(today.getTime() / DateHelper.dayInMs);
	}

	public static dateDiffWithoutTime(dateFrom: Date, dateTo: Date): Date {
		if (!dateFrom) {
			throw new InvalidParameterError("dateFrom", InvalidParameterError.nullText);
		}
		if (!dateTo) {
			throw new InvalidParameterError("dateTo", InvalidParameterError.nullText);
		}
		let from = DateHelper.getDateWithoutTime(dateFrom).getTime();
		let to = DateHelper.getDateWithoutTime(dateTo).getTime();
		let diff = to - from;
		return new Date(diff);
	}

	// Checks if given date and given days is within todays range (e.g. 01.01.18 with 9 days is withing range of 10.01.18)
	public static isPastDateWithoutTimeWithinRangeOfDays(date: Date, days?: number): boolean {
		if (!date) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}
		if (DateHelper.isDateWithoutTimeInFutureOrToday(date)) {
			return true;
		}

		let dateWithoutTime = DateHelper.getDateWithoutTime(date);

		// no days
		if (!days) {
			return false;
		}

		// check range
		return DateHelper.totalDaysOfDateWithoutTime(DateHelper.dateDiffWithoutTime(date, DateHelper.today())) <= days;
	}

	public static nowAsTicks(): number {
		let now = Date.now(); // tslint:disable-line:ban
		let shift = UrlHelper.getInt("db-time-diff-in-ms");
		return now + shift;
	}

	public static nowAsDateWithTime(): Date {
		return new Date(DateHelper.nowAsTicks());
	}

	public static now(): Date {
		return DateHelper.nowAsDateWithTime();
	}

	public static timeSince(start: Date | string, end?: Date | string) {
		if (!start) {
			throw new InvalidParameterError("end", InvalidParameterError.nullText);
		}
		start = new Date(start);
		if (!end) {
			end = DateHelper.now();
		} else {
			end = new Date(end);
		}

		return (end.getTime() - start.getTime());
	}

	public static today(): Date {
		let todayDate = DateHelper.getDateWithoutTime(DateHelper.nowAsDateWithTime());
		return todayDate;
	}

	public static fullYear(): number {
		return DateHelper.now().getFullYear();
	}

	// Convert german date string (e.g. 1.7. to full date of year -> 01.07.2018, and also 1.7.2016 -> 01.07.2016)
	public static getFullGermanDateStringFromDayAndMonthWithLeadingZeros(date: string): string {
		if (!date) { return ""; }
		let dateArr: string[] = date.toString().split(".");
		let tmpArray: string[] = [];

		tmpArray = dateArr.map((value, i) => {
			if (i <= 1 && value.length < 2) {
				value = "0" + value;
			}
			if (i === 2) {
				if (!value) {
					value = new Date().getFullYear().toString();
				} else if (value.length < 4) {
					value = StringHelper.pad(value, 4);
				}
			}
			return value;
		});
		return tmpArray[0] + (tmpArray[1] ? ("." + tmpArray[1]) : "") + (tmpArray[2] ? ("." + tmpArray[2]) : "");
	}

	public static getJsonFormattedDateString(date: Date | string): string {
		if (!date) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}

		if (!(date instanceof Date)) {
			return this.getJsonFormattedDateString(new Date(date));
		}

		return DateTime.fromJSDate(date).toISODate();
	}

	public static getJsonFormattedDateStringOrDefault(date: Date | string, defaultValue: string = null): string {
		if (date == null) {
			return defaultValue;
		}

		return this.getJsonFormattedDateString(date);
	}

	public static getJsonFormattedDateTimeString(date: Date | string): string {
		if (!date) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}

		if (!(date instanceof Date)) {
			return this.getJsonFormattedDateString(new Date(date));
		}

		return DateTime.fromJSDate(date).toISO();
	}

	public static getJsonFormattedDateTimeStringOrDefault(date: Date | string, defaultValue: string = null): string {
		if (date == null) {
			return defaultValue;
		}

		return this.getJsonFormattedDateTimeString(date);
	}

	public static formatISO8601(date: Date, withTime: boolean = false): string {
		if (!date) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}

		if (!(date instanceof Date)) {
			date = new Date(date);
		}
		let ret: any[] = [];

		let pad = (n: number) => {
			if (n < 10) {
				return "0".concat(String(n));
			}
			return String(n);
		};

		let isZeroTime = () => {
			return !withTime || date.getHours() + date.getMinutes() + date.getSeconds() + date.getMilliseconds() < 1;
		};

		ret.push(date.getFullYear());
		ret.push("-");
		ret.push(pad(date.getMonth() + 1));
		ret.push("-");
		ret.push(pad(date.getDate()));
		if (!isZeroTime()) {
			ret.push("T");
			ret.push(pad(date.getHours()));
			ret.push(":");
			ret.push(pad(date.getMinutes()));
			ret.push(":");
			ret.push(pad(date.getSeconds()));

			if (date.getMilliseconds()) {
				ret.push(".");
				ret.push(date.getMilliseconds());
			}
		}
		ret.push("Z");
		return ret.join("");
	}

	public static formatISO8601OrDefault(date: Date | string, withTime: boolean = false, defaultValue: string = null): string {
		if (date == null) {
			return defaultValue;
		}

		return this.formatISO8601(new Date(date), withTime);
	}

	public static getYearFromDate(date: string | Date): number {
		if (date == null) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}

		return new Date(date).getFullYear();
	}

	public static getFullYear(year: number | string) {
		if (year == null) {
			throw new InvalidParameterError("year", InvalidParameterError.nullText);
		}
		let newYear: number = null;
		if (typeof year === "string") {
			newYear = parseInt(year);
		} else {
			newYear = year;
		}

		if (newYear >= 1000) {
			return newYear;
		} else {
			return 2000 + newYear;
		}
	}

	public static fromGermanDateStringToDate(date: string | Date) {
		if (!date) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}
		if (date instanceof Date) {
			return date;
		}
		let convertedDate = DateTime.fromFormat(date, "dd.MM.yyyy");
		if (!convertedDate.isValid) {
			throw new InvalidParameterError("date", "Date has not the correct format (should be dd.MM.yyyy)");
		}
		return convertedDate.toJSDate();
	}

	public static fromGermanDateStringToDateWithDayMonthYear(date: string) {
		if (!date) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}

		let day: number = +(new Date(date).getDate());
		let month: number = +(new Date(date).getMonth() + 1);
		let year: number = +(new Date(date).getFullYear());
		return (day <= 9 ? ("0" + day) : day) + "." + (month <= 9 ? ("0" + month) : month) + "." + year;
	}

	public static getDayMonthToString(date: Date) {
		if (!date) {
			throw new InvalidParameterError("date", InvalidParameterError.nullText);
		}

		const day = date.getDate();
		const month = date.getMonth() + 1;

		return `${day <= 9 ? ("0" + day) : day}.${month <= 9 ? ("0" + month) : month}`;
	}

	public static firstDayDateOfMonth(curDate?: Date | string): Date {
		const date = curDate != null ? new Date(curDate) : new Date();
		const day = 1;
		const month = date.getMonth();
		const year = date.getFullYear();

		return new Date(year, month, day);
	}

	public static lastDayDateOfLastMonth(curDate?: Date | string): Date {
		const firstDayOfThisMonth = DateHelper.firstDayDateOfMonth(curDate);
		return DateHelper.addDays(firstDayOfThisMonth, -1);
	}

	public static monthWithFirsDay(): string {
		return DateHelper.firstDayDateOfMonth().toString();
	}

	public static getCalendarWeek(curDate?: Date | string) {
		let date = curDate != null ? new Date(curDate) : new Date();
		date = DateHelper.getDateWithoutTime(date);
		// Thursday in current week decides the year.
		date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
		// January 4 is always in week 1.
		let week1 = new Date(date.getFullYear(), 0, 4);
		// Adjust to Thursday in week 1 and count number of weeks from date to week1.
		return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000
			- 3 + (week1.getDay() + 6) % 7) / 7);
	}

	public static dateWithLastDayFromMonth(curDate: Date) {
		const date = curDate ?? new Date();
		const month = date.getMonth();
		const year = date.getFullYear();

		let nextMonth = (month >= 0 && month <= 11) ? month + 1 : month;
		let dateWithFirstDayFromLastManth = DateHelper.firstDayDateOfMonth(new Date(year, nextMonth, 1));

		return DateHelper.addDays(dateWithFirstDayFromLastManth, -1);
	}

	public static dateWithNextMonthAsISOSString() {
		const date = DateHelper.nowAsDateWithTime();
		const month = date.getMonth();
		const year = date.getFullYear();
		const day = date.getDate();

		let nextMonth = (month >= 0 && month <= 11) ? month + 1 : month;
		let result = new Date(year, nextMonth, 1);

		return result.toISOString();
	}

	public static lastMonthBeforeDayOrCurrentMonth(day: number = 10): Date {
		const date = DateHelper.nowAsDateWithTime();
		if (date.getDate() <= day) {
			// take last month
			return DateHelper.lastDayDateOfLastMonth(date);
		} else {
			return date;
		}
	}
}